import { createSlug } from '../../util/urlHelpers';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';

// Number of live listing suggestions shown in the keyword autocomplete.
const SUGGESTION_PAGE_SIZE = 5;
const MIN_QUERY_LENGTH = 2;

// ================ Action types ================ //

export const FETCH_SUGGESTIONS_REQUEST =
  'app/TopbarContainer/FETCH_SUGGESTIONS_REQUEST';
export const FETCH_SUGGESTIONS_SUCCESS =
  'app/TopbarContainer/FETCH_SUGGESTIONS_SUCCESS';
export const FETCH_SUGGESTIONS_ERROR =
  'app/TopbarContainer/FETCH_SUGGESTIONS_ERROR';
export const CLEAR_SUGGESTIONS = 'app/TopbarContainer/CLEAR_SUGGESTIONS';

// ================ Reducer ================ //

const initialState = {
  suggestionListings: [],
  suggestionsInProgress: false,
  suggestionsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SUGGESTIONS_REQUEST:
      return { ...state, suggestionsInProgress: true, suggestionsError: null };
    case FETCH_SUGGESTIONS_SUCCESS:
      return {
        ...state,
        suggestionsInProgress: false,
        suggestionListings: payload.listings,
      };
    case FETCH_SUGGESTIONS_ERROR:
      return {
        ...state,
        suggestionsInProgress: false,
        suggestionsError: payload,
      };
    case CLEAR_SUGGESTIONS:
      return { ...initialState };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchSuggestionsRequest = () => ({ type: FETCH_SUGGESTIONS_REQUEST });
export const fetchSuggestionsSuccess = listings => ({
  type: FETCH_SUGGESTIONS_SUCCESS,
  payload: { listings },
});
export const fetchSuggestionsError = e => ({
  type: FETCH_SUGGESTIONS_ERROR,
  error: true,
  payload: e,
});
export const clearSuggestions = () => ({ type: CLEAR_SUGGESTIONS });

// ================ Thunks ================ //

// Monotonic request id used as a "takeLatest" guard: responses for stale
// queries are ignored so the dropdown never flickers back to older results
// when the user is typing quickly.
let latestRequestId = 0;

// Project a denormalised listing entity down to just what a suggestion row
// needs. Keeping this minimal avoids coupling to marketplaceData sanitization.
const toSuggestion = listing => {
  const { id, attributes, images } = listing;
  const title = attributes?.title || '';
  const firstImage = images && images.length > 0 ? images[0] : null;
  const imageUrl = firstImage?.attributes?.variants?.['scaled-small']?.url || null;
  return {
    id: id?.uuid,
    title,
    slug: createSlug(title),
    price: attributes?.price || null,
    imageUrl,
    // Free-text public data used to derive brand/designer suggestion chips.
    brand: attributes?.publicData?.brand || null,
    designer: attributes?.publicData?.designer || null,
  };
};

/**
 * Fetch live listing suggestions for the keyword autocomplete.
 * Debouncing is handled in the input component; here we only guard against
 * out-of-order responses and short queries.
 */
export const fetchSearchSuggestions = keywords => (dispatch, getState, sdk) => {
  const trimmed = (keywords || '').trim();
  if (trimmed.length < MIN_QUERY_LENGTH) {
    dispatch(clearSuggestions());
    return Promise.resolve([]);
  }

  const requestId = ++latestRequestId;
  dispatch(fetchSuggestionsRequest());

  return sdk.listings
    .query({
      keywords: trimmed,
      perPage: SUGGESTION_PAGE_SIZE,
      include: ['images'],
      'fields.listing': [
        'title',
        'price',
        'publicData.brand',
        'publicData.designer',
      ],
      'fields.image': ['variants.scaled-small'],
      'limit.images': 1,
    })
    .then(response => {
      // Ignore stale responses.
      if (requestId !== latestRequestId) {
        return [];
      }
      const listings = denormalisedResponseEntities(response).map(toSuggestion);
      dispatch(fetchSuggestionsSuccess(listings));
      return listings;
    })
    .catch(e => {
      if (requestId === latestRequestId) {
        dispatch(fetchSuggestionsError(storableError(e)));
      }
    });
};
